import type { City, EquineData, EquinePoint, Feature, FixturesData, FoodPoint, GaaPoint, LayerId, LayerPoint, NaturePoint, SessionPoint, Site, TrainPoint } from "../types";
import { LAYERS, LAYER_ORDER, PERIOD_COLORS } from "./layers";
import { buildIndex } from "./search";
import { cities, dataError, equineData, featuresByLayer, fixturesData, layerLoaded } from "../state/store";

// Data is served from /data/ (a copy of ../data made at build time; in dev, Vite's
// fs.allow lets us alias it — see main.tsx for the dev path).
const RAW_BASE = import.meta.env.VITE_DATA_URL || import.meta.env.BASE_URL + "data/";
export const DATA_BASE = RAW_BASE.endsWith("/") ? RAW_BASE : RAW_BASE + "/";

async function fetchJSON<T>(file: string): Promise<T> {
  const res = await fetch(DATA_BASE + file, { cache: "default" });
  if (!res.ok) throw new Error(`${file}: HTTP ${res.status}`);
  // Dev servers answer unknown paths with index.html (200) — treat that as missing.
  const ct = res.headers.get("content-type") || "";
  if (!/json/i.test(ct)) throw new Error(`${file}: not JSON (${ct.split(";")[0] || "no content-type"})`);
  return res.json() as Promise<T>;
}

export function siteToFeature(s: Site): Feature {
  const period = s.periods?.[0] ?? "default";
  return {
    key: `history:${s.id}`,
    layer: "history",
    id: s.id,
    name: s.general_name,
    sub: s.general_name_en && s.general_name_en !== s.general_name ? s.general_name_en : undefined,
    lat: s.lat,
    lon: s.lon,
    color: PERIOD_COLORS[period] ?? PERIOD_COLORS.default,
    rank: s.rank ?? 1,
    mapName: s.map_name,
    dateStart: s.date_start,
    dateEnd: s.date_end,
    county: s.county,
    raw: s,
  };
}

export function pointToFeature(layer: Exclude<LayerId, "history" | "sessions">, p: LayerPoint): Feature {
  return {
    key: `${layer}:${p.id}`,
    layer,
    id: p.id,
    name: p.name,
    sub: p.name_ga && p.name_ga !== p.name ? p.name_ga : p.county,
    lat: p.lat,
    lon: p.lon,
    color: LAYERS[layer].color,
    rank: p.rank ?? 1,
    mapName: p.map_name,
    county: p.county,
    raw: p as GaaPoint | EquinePoint | FoodPoint | NaturePoint | TrainPoint,
  };
}

export function sessionToFeature(s: SessionPoint): Feature {
  return {
    key: `sessions:${s.id}`,
    layer: "sessions",
    id: s.id,
    name: s.name,
    sub: [s.town, s.county].filter(Boolean).join(", "),
    lat: s.lat,
    lon: s.lon,
    color: LAYERS.sessions.color,
    rank: s.rank ?? 1,
    mapName: s.map_name,
    county: s.county ?? undefined,
    raw: s,
  };
}

function setLayer(id: LayerId, feats: Feature[]): void {
  for (const f of feats) buildIndex(f);
  featuresByLayer.value = { ...featuresByLayer.value, [id]: feats };
  layerLoaded.value = { ...layerLoaded.value, [id]: true };
}

/** Load the history layer + cities first (they render the initial map), then the rest. */
export async function loadAllData(): Promise<void> {
  dataError.value = null;
  try {
    const [sites, cityList] = await Promise.all([fetchJSON<Site[]>(LAYERS.history.file), fetchJSON<City[]>("cities.json")]);
    cities.value = cityList;
    setLayer("history", sites.map(siteToFeature));
  } catch (err) {
    dataError.value = `Site data failed to load (${err instanceof Error ? err.message : String(err)}). Run \`npm run merge\` in the project root.`;
    throw err;
  }
  // Secondary layers load in parallel; a missing one just stays empty.
  await Promise.all([
    loadFixtures(),
    loadEquine(),
    ...LAYER_ORDER.filter((id) => id !== "history").map(async (id) => {
      try {
        const rows = await fetchJSON<unknown[]>(LAYERS[id].file);
        const feats = id === "sessions"
          ? (rows as SessionPoint[]).map(sessionToFeature)
          : (rows as LayerPoint[]).map((p) => pointToFeature(id as Exclude<LayerId, "history" | "sessions">, p));
        setLayer(id, feats);
      } catch (err) {
        console.warn(`layer ${id} not available:`, err);
        layerLoaded.value = { ...layerLoaded.value, [id]: true };
      }
    }),
  ]);
}

/** GAA fixtures + the season skeleton (scripts/build-fixtures.mjs). Optional: the map works without it. */
export async function loadFixtures(): Promise<void> {
  try {
    fixturesData.value = await fetchJSON<FixturesData>("gaa-fixtures.json");
  } catch (err) {
    console.warn("GAA fixtures not available:", err);
  }
}

/** Racing and eventing fixtures (scripts/build-equine.mjs). Optional, same as the GAA feed. */
export async function loadEquine(): Promise<void> {
  try {
    equineData.value = await fetchJSON<EquineData>("equine-events.json");
  } catch (err) {
    console.warn("Equine events not available:", err);
  }
}
